import { useState, useEffect } from 'react';
import { Headphones, Clock, CheckCircle, AlertTriangle, User, ChevronRight, Send, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { formatDistanceToNow } from 'date-fns';

const STATUS_CFG = {
  open:         { color: '#F5A623', label: 'Open' },
  assigned:     { color: '#4DA2FF', label: 'Assigned' },
  in_progress:  { color: '#FF6B00', label: 'In Progress' },
  waiting_user: { color: '#9945FF', label: 'Waiting' },
  resolved:     { color: '#00C864', label: 'Resolved' },
  closed:       { color: '#6B6B8A', label: 'Closed' },
};

const PRIORITY_CFG = {
  low:    { color: '#6B6B8A', dot: '⚪' },
  medium: { color: '#4DA2FF', dot: '🔵' },
  high:   { color: '#F5A623', dot: '🟡' },
  urgent: { color: '#FF3232', dot: '🔴' },
};

export default function CSDashboard() {
  const [tab, setTab] = useState('unassigned');
  const [tickets, setTickets] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [stats, setStats] = useState({});
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, u, t] = await Promise.all([
        api.get('/support/cs/stats'),
        api.get('/support/cs/unassigned'),
        api.get('/support/cs/tickets'),
      ]);
      setStats(s.data.stats || {});
      setUnassigned(u.data.tickets || []);
      setTickets(t.data.tickets || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const openTicket = async (ticket) => {
    const res = await api.get(`/support/tickets/${ticket._id}`);
    setSelected(res.data.ticket);
  };

  const assignToMe = async (ticketId) => {
    try {
      await api.patch(`/support/cs/tickets/${ticketId}/assign`);
      toast.success('Ticket assigned to you');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${selected._id}/reply`, { message: reply, isInternal });
      setReply('');
      setIsInternal(false);
      openTicket(selected);
      toast.success(isInternal ? 'Internal note added' : 'Reply sent to customer');
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const resolveTicket = async () => {
    const resolution = prompt('Resolution summary:');
    if (!resolution) return;
    try {
      await api.patch(`/support/cs/tickets/${selected._id}/resolve`, { resolution });
      toast.success('Ticket resolved');
      setSelected(null);
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  if (selected) {
    const cfg = STATUS_CFG[selected.status] || STATUS_CFG.open;
    return (
      <div className="min-h-screen pt-20 pb-36 px-4 max-w-2xl mx-auto">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-brand-muted text-sm mt-4 mb-4 hover:text-white">
          ← Back
        </button>
        <div className="glass rounded-2xl p-4 mb-4">
          <div className="flex justify-between mb-2">
            <div>
              <p className="font-bold">{selected.subject}</p>
              <p className="text-xs font-mono text-brand-muted">#{selected.ticketNumber}</p>
              <p className="text-xs text-brand-muted mt-1">From: <strong className="text-brand-text">{selected.user?.username}</strong> ({selected.user?.email})</p>
            </div>
            <span className="badge text-xs" style={{ background: `${cfg.color}15`, color: cfg.color }}>{cfg.label}</span>
          </div>
          {!['resolved','closed'].includes(selected.status) && (
            <button onClick={resolveTicket} className="mt-2 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1"
              style={{ background: 'rgba(0,200,100,0.1)', color: '#00C864', border: '1px solid rgba(0,200,100,0.3)' }}>
              <CheckCircle size={12} /> Mark Resolved
            </button>
          )}
        </div>

        <div className="space-y-3 mb-6">
          {selected.messages?.map((msg, i) => {
            const isCustomer = ['buyer','seller'].includes(msg.senderRole);
            return (
              <div key={i} className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.isInternal ? 'border-dashed' : ''}`}
                  style={{
                    background: msg.isInternal ? 'rgba(245,166,35,0.08)' : isCustomer ? '#12121A' : 'linear-gradient(135deg,#FF6B00,#F5A623)',
                    color: msg.isInternal ? '#F5A623' : isCustomer ? '#E0E0F0' : 'white',
                    border: msg.isInternal ? '1px dashed rgba(245,166,35,0.4)' : isCustomer ? '1px solid #1E1E2E' : 'none',
                    borderTopLeftRadius: isCustomer ? 4 : 16,
                    borderTopRightRadius: isCustomer ? 16 : 4,
                  }}>
                  {msg.isInternal && <p className="text-xs font-bold mb-1">🔒 Internal Note</p>}
                  {isCustomer && <p className="text-xs font-bold mb-1" style={{ color: '#FF6B00' }}>{msg.sender?.username}</p>}
                  <p>{msg.message}</p>
                  <p className="text-xs mt-1 opacity-60">{msg.createdAt ? formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true }) : ''}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="fixed bottom-16 left-0 right-0 px-4 pb-4 max-w-2xl mx-auto" style={{ background: 'linear-gradient(transparent, #0A0A0F 30%)' }}>
          <div className="mb-2 flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer text-xs text-brand-muted">
              <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} className="accent-yellow-500" />
              🔒 Internal note (customer won't see this)
            </label>
          </div>
          <div className="flex gap-2 p-2 rounded-2xl" style={{ background: isInternal ? 'rgba(245,166,35,0.06)' : '#12121A', border: `1px solid ${isInternal ? 'rgba(245,166,35,0.3)' : '#1E1E2E'}` }}>
            <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder={isInternal ? 'Add internal note...' : 'Reply to customer...'}
              className="flex-1 bg-transparent text-sm resize-none outline-none p-2 min-h-[44px] max-h-32" style={{ color: '#E0E0F0' }} rows={1} />
            <button onClick={sendReply} disabled={!reply.trim() || sending}
              className="px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-1 flex-shrink-0 disabled:opacity-40"
              style={{ background: isInternal ? 'rgba(245,166,35,0.2)' : 'linear-gradient(135deg,#FF6B00,#F5A623)', color: isInternal ? '#F5A623' : 'white' }}>
              {sending ? <div className="spinner" /> : <><Send size={14} /> {isInternal ? 'Note' : 'Send'}</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mt-4 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#4DA2FF,#9945FF)' }}>
          <Headphones size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Sora' }}>Customer Service</h1>
          <p className="text-xs text-brand-muted">Support Staff Panel</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Open', value: stats.open, color: '#F5A623' },
          { label: 'In Progress', value: stats.inProgress, color: '#FF6B00' },
          { label: 'Resolved', value: stats.resolved, color: '#00C864' },
          { label: '🔴 Urgent', value: stats.unassignedUrgent, color: '#FF3232' },
        ].map(({ label, value, color }) => (
          <div key={label} className="p-3 rounded-xl text-center" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
            <p className="text-2xl font-bold" style={{ color, fontFamily: 'Sora' }}>{value ?? 0}</p>
            <p className="text-xs text-brand-muted">{label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 p-1 rounded-xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
        {[['unassigned','Unassigned'],['my','My Tickets']].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background: tab===id?'linear-gradient(135deg,#FF6B00,#F5A623)':'transparent', color: tab===id?'white':'#6B6B8A' }}>
            {label} {tab===id && <span className="opacity-70">({id==='unassigned'?unassigned.length:tickets.length})</span>}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="spinner" /></div> : (
        <div className="space-y-3">
          {(tab === 'unassigned' ? unassigned : tickets).map(ticket => {
            const scfg = STATUS_CFG[ticket.status] || STATUS_CFG.open;
            const pcfg = PRIORITY_CFG[ticket.priority] || PRIORITY_CFG.medium;
            return (
              <div key={ticket._id} className="p-4 rounded-2xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span>{pcfg.dot}</span>
                      <p className="font-semibold text-sm truncate">{ticket.subject}</p>
                    </div>
                    <p className="text-xs text-brand-muted font-mono">#{ticket.ticketNumber}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-brand-muted">
                      <span><User size={10} className="inline mr-1" />{ticket.user?.username}</span>
                      <span>{ticket.category?.replace('_',' ')}</span>
                      <span>{formatDistanceToNow(new Date(ticket.createdAt), { addSuffix: true })}</span>
                    </div>
                  </div>
                  <span className="badge text-xs flex-shrink-0" style={{ background: `${scfg.color}15`, color: scfg.color }}>{scfg.label}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  {tab === 'unassigned' && (
                    <button onClick={() => assignToMe(ticket._id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                      style={{ background: 'rgba(255,107,0,0.1)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' }}>
                      Assign to Me
                    </button>
                  )}
                  <button onClick={() => openTicket(ticket)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1"
                    style={{ background: '#1E1E2E', color: '#E0E0F0' }}>
                    <MessageCircle size={12} /> Open <ChevronRight size={12} />
                  </button>
                </div>
              </div>
            );
          })}
          {(tab === 'unassigned' ? unassigned : tickets).length === 0 && (
            <div className="text-center py-16 text-brand-muted">
              <CheckCircle size={40} className="mx-auto mb-3 opacity-30" />
              <p>{tab === 'unassigned' ? 'No unassigned tickets 🎉' : 'No tickets assigned to you'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
